import type { QueryCondition, QueryOperator } from '@edulead/models';
import General from '../../utils/GeneralUtils';
import { withDefaultLastSeen } from '../../utils/peopleSearchDefaults';
import {
  hasFilterValue,
  isFilterSet,
  isRequirementMet,
  NAVIGATOR_FILTERS,
  NavigatorSectionId,
} from './navigator-filters';

/** The query: its rules in the panel's order. A filter may be in it more than once (State is
 * X, State is not Y), and a rule with no value is not part of a search. */
export type NavigatorQuery = NavigatorRule[];

/**
 * One rule of the query in the URL: the query editor's rule (field, operator, value) with the
 * section it belongs to, since Institutions and People share some field names (name, phone).
 */
export interface NavigatorRule {
  section: NavigatorSectionId;
  field: string;
  operator: string;
  value: string;
  /** Switched off: kept in the URL for the panel, left out of the searches. */
  disabled?: boolean;
}

/** Whether the URL carries a query at all: no ?q= is no search, an empty one is everyone. */
export const hasNavigatorQuery = (search: string): boolean => new URLSearchParams(search).has('q');

/**
 * The query from the URL's ?q= (base64 JSON, as the directories'): the rules of filters the
 * panel has; empty when missing or bad.
 */
export function parseNavigatorQuery(search: string): NavigatorQuery {
  const decoded = General.decode(new URLSearchParams(search).get('q') ?? undefined) as {
    rules?: unknown;
  } | null;
  if (!Array.isArray(decoded?.rules)) return [];
  return (decoded.rules as Partial<NavigatorRule>[]).flatMap((rule) => {
    const filter = NAVIGATOR_FILTERS.find(
      (f) => f.section === rule.section && f.field === rule.field,
    );
    return filter && typeof rule.operator === 'string'
      ? [
          {
            section: filter.section,
            field: filter.field,
            operator: rule.operator,
            value: String(rule.value ?? ''),
            ...(rule.disabled === true ? { disabled: true } : {}),
          },
        ]
      : [];
  });
}

/** The filter a rule is of. */
const filterOf = (rule: NavigatorRule) =>
  NAVIGATOR_FILTERS.find((f) => f.section === rule.section && f.field === rule.field);

/**
 * The rules that are set, not switched off, and whose filter's requirement is (a radius without
 * beacons is not): the ones a search takes.
 */
export function navigatorRules(query: NavigatorQuery): NavigatorRule[] {
  const set = query.filter((rule) => isFilterSet(rule));
  return set.filter((rule) => {
    const filter = filterOf(rule);
    return !filter || isRequirementMet(filter, set);
  });
}

/**
 * The query as the URL's ?q=: the rules with a value, all of them required; the switched-off
 * ones too, marked, so the panel shows them again. The searches read it through navigatorRules.
 */
export function serializeNavigatorQuery(query: NavigatorQuery): string {
  return (
    General.encode({
      combinator: 'and',
      rules: query
        .filter((rule) => hasFilterValue(rule))
        .map(({ disabled, ...rule }) => (disabled ? { ...rule, disabled } : rule)),
    }) ?? ''
  );
}

/**
 * The query the Navigator opens with when the URL has none: technology people at public
 * institutions in California and North Carolina, so the map has something to show at once.
 */
export const DEFAULT_NAVIGATOR_QUERY: NavigatorQuery = [
  { section: 'location', field: 'state', operator: 'equals', value: 'CA,NC' },
  { section: 'institutions', field: 'access', operator: 'equals', value: 'PUBLIC' },
  { section: 'people', field: 'role.functionalGroup', operator: 'equals', value: 'technology' },
];

/**
 * A rule as a condition, the value a list for the list operators (as convertQtoQuery) and a
 * number for the filters the searches read only as one (radiusMiles).
 */
const toCondition = (field: string, rule: NavigatorRule): QueryCondition => {
  let value: QueryCondition['value'] = rule.value;
  if (rule.operator === 'in' || rule.operator === 'not_in') {
    value = rule.value
      .split(',')
      .map((it) => it.trim())
      .filter((it) => !!it);
  } else if (filterOf(rule)?.isNumberValue) {
    value = Number(rule.value);
  }
  return { field, operator: rule.operator as QueryOperator, value };
};

/**
 * The institution filters' names in the people search, where an institution's field is a field
 * of the person's institution. Missing: not searchable on people, so the rule is left out
 * (phone, fax, the parent institution) until the Go search takes it.
 */
const PEOPLE_INSTITUTION_FIELDS: Record<string, string> = {
  type: 'institutionType',
  name: 'institutionName',
  grade: 'grade',
  subtype: 'subtype',
  access: 'access',
  model: 'model',
  religiousAffiliation: 'religiousAffiliation',
  titleOne: 'titleOne',
  studentsTotal: 'studentsTotal',
  stfTotal: 'stfTotal',
  districtRevTotal: 'districtRevTotal',
  fulltext: 'fulltext',
  ncesId: 'ncesId',
  institutionId: 'institutionId',
};

/**
 * The location filters the searches take as they are, beacons included (`beaconIds in [...]`,
 * api/v4/beacons.md §12): the people at the beacons' institutions, the institutions themselves.
 * Territories too (`territoryId in [...]`, api/v4/territories.md §12): both searches resolve
 * them to their states together.
 */
const SEARCHABLE_LOCATION_FIELDS = new Set([
  'state',
  'zip',
  'countyId',
  'territoryId',
  'shapes',
  'radiusSearch',
  'beaconIds',
]);
const INSTITUTIONS_LOCATION_FIELDS = SEARCHABLE_LOCATION_FIELDS;
const PEOPLE_LOCATION_FIELDS = SEARCHABLE_LOCATION_FIELDS;

/**
 * The query as the people search's conditions: the people rules as they are, the institution
 * rules under the person's institution fields, the location rules the search takes, and the
 * directory's hidden 6-month Last seen unless one is set.
 */
export function navigatorPeopleConditions(query: NavigatorQuery): QueryCondition[] {
  const conditions = navigatorRules(query).flatMap((rule): QueryCondition[] => {
    switch (rule.section) {
      case 'people':
        return [toCondition(rule.field, rule)];
      case 'institutions': {
        const field = PEOPLE_INSTITUTION_FIELDS[rule.field];
        return field ? [toCondition(field, rule)] : [];
      }
      case 'location':
        return PEOPLE_LOCATION_FIELDS.has(rule.field) ? [toCondition(rule.field, rule)] : [];
      case 'expansion':
        return [toCondition(rule.field, rule)];
    }
  });
  return withDefaultLastSeen(conditions);
}

/** The set filters the people search cannot take yet, by label, to say so above the list. */
export function navigatorPeopleUnsupported(query: NavigatorQuery): string[] {
  return navigatorRules(query).flatMap((rule) => {
    const isTaken =
      rule.section === 'people' ||
      rule.section === 'expansion' ||
      (rule.section === 'institutions' && !!PEOPLE_INSTITUTION_FIELDS[rule.field]) ||
      (rule.section === 'location' && PEOPLE_LOCATION_FIELDS.has(rule.field));
    if (isTaken) return [];
    const filter = NAVIGATOR_FILTERS.find(
      (f) => f.section === rule.section && f.field === rule.field,
    );
    return filter ? [filter.label] : [];
  });
}

/**
 * The institution filters the institution search does not take: the district's revenue and the
 * Edulead ID are contact-side fields of the person's institution with no counterpart there.
 */
const INSTITUTIONS_UNSEARCHABLE_FIELDS = new Set(['districtRevTotal', 'institutionId']);

/**
 * The query as the institution search's conditions: the institution rules under their own
 * names and the location rules the search takes. The people rules do not apply: an institution
 * is listed whether or not it has matching people.
 */
export function navigatorInstitutionsConditions(query: NavigatorQuery): QueryCondition[] {
  return navigatorRules(query).flatMap((rule): QueryCondition[] => {
    switch (rule.section) {
      case 'people':
        return [];
      case 'institutions':
        return INSTITUTIONS_UNSEARCHABLE_FIELDS.has(rule.field)
          ? []
          : [toCondition(rule.field, rule)];
      case 'location':
        return INSTITUTIONS_LOCATION_FIELDS.has(rule.field) ? [toCondition(rule.field, rule)] : [];
      case 'expansion':
        return [toCondition(rule.field, rule)];
    }
  });
}

/** The set institution and location filters the institution search cannot take yet, by label. */
export function navigatorInstitutionsUnsupported(query: NavigatorQuery): string[] {
  return navigatorRules(query).flatMap((rule) => {
    const isDropped =
      (rule.section === 'institutions' && INSTITUTIONS_UNSEARCHABLE_FIELDS.has(rule.field)) ||
      (rule.section === 'location' && !INSTITUTIONS_LOCATION_FIELDS.has(rule.field));
    if (!isDropped) return [];
    const filter = NAVIGATOR_FILTERS.find(
      (f) => f.section === rule.section && f.field === rule.field,
    );
    return filter ? [filter.label] : [];
  });
}
